import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FiCalendar, FiChevronLeft, FiCreditCard, FiGift, FiUser } from 'react-icons/fi';

import NoYachtFallback from '@/assets/no-yacht-fallback-new.svg';
import { Avatar } from '@/components';
import {
  BackButton,
  CountChip,
  DetailHeader,
  EmptyState,
  Group,
  GroupLabel,
  Row,
} from '@/design-system/Layout';
import type { Subscription } from '@/store/subscriptions/subscriptions.types';

import { cycleUnit, fmtDate } from '../subscription.helpers';
import s from '../subscription.module.scss';
import { seatOffer } from '../subscription.seats';
import type { SeatPool } from '../subscription.seats';
import type { Vessel } from '../subscription.types';
import { canMove, hasFreeSeat } from '../useVesselSeats';
import type { VesselSeats } from '../useVesselSeats';

import { StatusChip } from './Chips';
import { MoveSeatModal } from './MoveSeatModal';
import { GuestRow, NoSeatRow, SEAT_AVATAR_SIZE, SeatRow } from './SeatRow';
import type { SeatDrag, SeatTarget } from './SeatRow';

export function VesselDetail({
  vessel,
  subscription,
  bill,
  seats,
  planAction,
  paidByAnother = false,
  onBack,
}: {
  vessel: Vessel;
  /**
   * The live subscription row — what a full seat pool is bought onto. `null` when the viewer
   * may not buy onto it: no `EditSubscription`, a lifetime plan, or a plan on somebody else's
   * card, where `PATCH` would re-price theirs — see `subscription.takeover`.
   */
  subscription: Subscription | null;
  /**
   * The formatted charge for one period, e.g. `"$29"`. `null` while it is unknown — no
   * amount is shown then rather than a guess.
   */
  bill?: string | null;
  /** The vessel's seat pools: who holds a seat, and which are still open. */
  seats: VesselSeats;
  /** The vessel's plan control — Change plan, or Take over plan — shown beside the bill. */
  planAction?: ReactNode;
  /**
   * The plan is real and somebody else pays for it — a captain who bought it, most often.
   * Said out loud because the amount above it is otherwise read as the viewer's own bill.
   */
  paidByAnother?: boolean;
  onBack: () => void;
}) {
  const unit = cycleUnit(vessel.cycle);
  // An admin granted the plan: there is no bill, so the hero names the plan instead of an amount.
  const isLifetime = vessel.status === 'lifetime';

  // A small threshold keeps a plain click on a face meaning "open its seat menu"; on touch, a
  // long press starts the drag so a swipe across the faces still scrolls the page.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );
  // Pools are held by name and read back from `seats` on every render, so a refetch that lands
  // mid-drag or while a plain move's confirm is open is reflected rather than a stale copy. A
  // confirm that offers a seat to buy holds its own copy instead — see `MoveSeatModal`.
  const [dragging, setDragging] = useState<SeatDrag | null>(null);
  const [moving, setMoving] = useState<(SeatDrag & { to: SeatPool }) | null>(null);
  const poolOf = (pool: SeatPool | null) => seats.pools.find((state) => state.pool === pool);

  // Whether a face may land in a pool — asked by a drag, and by the face's menu for its moves.
  const canDrop = (drag: SeatDrag, pool: SeatPool) => {
    const from = poolOf(drag.pool);
    const to = poolOf(pool);
    // A full pool still takes the drop when a seat can be bought or the plan upgraded —
    // the confirm then offers that instead of a plain move. No `from` is someone with no seat.
    return (
      !!to && canMove(drag.person, from, to) && (hasFreeSeat(to) || !!seatOffer(subscription, pool))
    );
  };
  const accepts = (pool: SeatPool) => !!dragging && canDrop(dragging, pool);
  const moveTargets = (drag: SeatDrag): SeatTarget[] =>
    seats.pools
      .filter(({ pool }) => canDrop(drag, pool))
      .map((state) => ({
        state,
        offer: hasFreeSeat(state) ? null : seatOffer(subscription, state.pool),
      }));

  const onMove = (drag: SeatDrag, to: SeatPool) => setMoving({ ...drag, to });

  const onDragStart = ({ active }: DragStartEvent) =>
    setDragging((active.data.current as SeatDrag | undefined) ?? null);

  const onDragEnd = ({ over }: DragEndEvent) => {
    const to = over?.id as SeatPool | undefined;
    if (dragging && to && accepts(to)) setMoving({ ...dragging, to });
    setDragging(null);
  };

  const movingTo = moving && poolOf(moving.to);

  const { renewal } = vessel;
  const billLine = isLifetime
    ? 'Granted by Vessel Haven · never billed'
    : [
        seats.capacity === 0
          ? 'Nothing billed'
          : vessel.cycle === 'yearly'
            ? 'Billed yearly · save ~17%'
            : 'Billed monthly',
        renewal.at && `${renewal.kind === 'ends' ? 'ends' : 'renews'} ${fmtDate(renewal.at)}`,
      ]
        .filter(Boolean)
        .join(' · ');

  const seatRows = () => {
    if (seats.isLoading) return <EmptyState>Loading seats…</EmptyState>;
    if (seats.error) return <EmptyState>Could not load who holds a seat.</EmptyState>;
    // Covers no subscription row, a dead one, and harbor — the copy states the fact and
    // leaves the plan control above to offer the next step.
    if (seats.capacity === 0) return <EmptyState>This plan includes no seats.</EmptyState>;

    // A pool the tier does not sell is left out entirely rather than shown as 0/0. One it
    // sells but holds none of yet stays, as the place to drop someone to buy the first seat.
    return (
      <>
        {seats.pools
          .filter((pool) => pool.capacity > 0 || seatOffer(subscription, pool.pool) === 'buy')
          .map((pool) => (
            <SeatRow
              key={pool.pool}
              state={pool}
              vesselName={vessel.name}
              serviceId={vessel.id}
              accepts={accepts(pool.pool)}
              moveTargets={moveTargets}
              onMove={onMove}
            />
          ))}
        <NoSeatRow
          people={seats.unseated}
          serviceId={vessel.id}
          canManage={seats.pools.some((pool) => pool.canManage)}
          moveTargets={moveTargets}
          onMove={onMove}
        />
        <GuestRow people={seats.guests} />
      </>
    );
  };

  return (
    <>
      <BackButton onClick={onBack}>
        <FiChevronLeft /> Manage your account
      </BackButton>

      <DetailHeader
        leading={
          <Avatar
            src={vessel.image}
            fallbackSrc={NoYachtFallback}
            serviceId={vessel.id}
            firstName={vessel.name}
            size={56}
          />
        }
        title={
          <span className={s.titleWithPlan}>
            {vessel.name}
            <span className={s.titlePlan}>{vessel.plan}</span>
          </span>
        }
        trailing={<StatusChip status={vessel.status} />}
      />

      <Group>
        <div className={s.hero}>
          <div className={s.heroIcon}>
            <FiCreditCard />
          </div>
          <div className={s.heroText}>
            <div className={s.heroTitle}>
              {isLifetime ? 'Lifetime' : (bill ?? '—')}
              {!isLifetime && bill != null && <span className={s.heroUnit}>{unit}</span>}
            </div>
            <div className={s.heroSub}>{billLine}</div>
          </div>
          {planAction && <div className={s.rowActions}>{planAction}</div>}
        </div>

        {isLifetime && (
          <Row
            icon={<FiGift />}
            title="Lifetime plan"
            sub="Granted by Vessel Haven. It never expires and is never billed, so its plan and seats can’t be changed, taken over or cancelled here. Contact support to change it."
          />
        )}

        {paidByAnother && (
          <Row
            icon={<FiUser />}
            title="Paid by somebody else"
            sub="This plan is on another person’s card, so none of it is billed to you."
          />
        )}

        {/*
          Only stated when there is a date behind it. A subscription winding down shows
          when access stops and carries no amount — nothing further is charged.
        */}
        {renewal.at != null && (
          <Row
            icon={<FiCalendar />}
            title={renewal.kind === 'ends' ? 'Access ends' : 'Next payment'}
            sub={fmtDate(renewal.at)}
            trailing={
              renewal.kind === 'renews' && bill != null ? (
                <CountChip>{`${bill}${unit}`}</CountChip>
              ) : undefined
            }
          />
        )}
      </Group>

      <GroupLabel>
        Seats
        {seats.capacity > 0 && ` · ${seats.occupied}/${seats.capacity} assigned`}
      </GroupLabel>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragging(null)}
      >
        <Group>{seatRows()}</Group>
        {/*
          Portalled: the group's backdrop-filter makes it the containing block for anything
          fixed inside it, and its overflow would clip the face as it crosses the rows.
        */}
        {createPortal(
          <DragOverlay>
            {dragging && (
              <Avatar
                src={dragging.person.avatar}
                firstName={dragging.person.firstName}
                lastName={dragging.person.lastName}
                size={SEAT_AVATAR_SIZE}
              />
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>

      {moving && movingTo && (
        <MoveSeatModal
          open
          person={moving.person}
          from={poolOf(moving.pool)}
          to={movingTo}
          vessel={vessel}
          seats={seats}
          subscription={subscription}
          onClose={() => setMoving(null)}
        />
      )}
    </>
  );
}
